import { ConditionalDisplay } from '@rsos/base-components';
import { FIELD_SHARING } from '@rsos/capstone/src/constants/capabilities';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import ShareEvent from '../ShareEvent/ShareEvent';
import { ALERT_RESOURCE } from '../ShareEvent/constants/ShareEvent.types';

/**
 * Field Share entry point for alerts, mounted into the alert card header.
 *
 * Three independent gates, each its own condition so a hidden button reports
 * which one failed rather than just being absent:
 *
 * - `unite_sharing` + `field_sharing_0` — the same pair the call-side
 *   `useHasAccessToFieldSharing` uses, so an agency without Field Share at all
 *   never sees this button.
 * - `alert-field-sharing` — alert-specific rollout. Field Share previously
 *   supported calls only; this flag lets alert support ship dark and be enabled
 *   per environment without touching the call-side gate.
 *
 * The flag reads must come from `@rsos/feature-flags` (Datadog). `portal_renderer`
 * does not populate the legacy Hydra `@rsos/flags`, so reading from there would
 * resolve to undefined and hide the button unconditionally.
 */
const AlertShareButton = () => {
  const uniteSharingFlag = useBooleanFlagValue('unite_sharing', false);
  const alertFieldSharingFlag = useBooleanFlagValue(
    'alert-field-sharing',
    false,
  );
  const fieldSharingCapability = useHasCapability(FIELD_SHARING);

  return (
    <ConditionalDisplay
      dataName="alert-field-share-button"
      conditions={[
        {
          showIf: Boolean(uniteSharingFlag),
          reason: 'Needs the unite_sharing feature flag',
        },
        {
          showIf: Boolean(alertFieldSharingFlag),
          reason:
            'Needs the alert-field-sharing feature flag (Field Share for alerts)',
        },
        {
          showIf: Boolean(fieldSharingCapability?.hasCapability),
          reason:
            'Agency is missing the field_sharing_0 capability (Andromeda > account > capabilities)',
        },
      ]}
    >
      <ShareEvent resource={ALERT_RESOURCE} isLabelHidden />
    </ConditionalDisplay>
  );
};

export default AlertShareButton;
